<template>
  <form @submit.prevent="onSubmit" class="form">
    <p class="form__title">
      ID:
      <span v-if="tag"
        ><a :href="tagPageLink" target="_blank">{{ tag.id }}</a></span
      >
    </p>
    <label class="form__item form-item">
      <p class="form-item__title">名前</p>
      <input
        type="text"
        name="name"
        v-model="editingTag.name"
        class="form-item__input"
      />
    </label>
    <label class="form__item form-item">
      <p class="form-item__title">説明</p>
      <textarea
        name="description"
        v-model="editingTag.description"
        class="form-item__input"
      />
    </label>
    <label class="form__item form-item">
      <p class="form-item__title">親カテゴリ</p>
      <input
        type="text"
        name="parent"
        v-model="editingParentCategoryName"
        class="form-item__input"
        list="tag-editor-options"
        :disabled="!isCategory"
      />
      <datalist id="tag-editor-options">
        <option value="" label="親カテゴリなし" />
        <option
          v-for="category in otherCategories"
          :key="category.id"
          :value="category.name"
          :label="`カテゴリID: ${category.id}`"
        />
      </datalist>
      <p v-if="!isParentTagValid" class="form-item__alert">
        そんなカテゴリないです
      </p>
      <p v-if="hasMovedTagCircularReference" class="form-item__alert">
        カテゴリ移動するとループが発生するので移動できないです
      </p>
      <p
        v-else-if="!isCategoryDepthUnderMaxSizeAfterMove"
        class="form-item__alert"
      >
        カテゴリ移動すると4層より深くなるので移動できないです
      </p>
    </label>
    <footer class="form__footer footer">
      <button
        type="button"
        :disabled="!isDeleteButtonEnabled"
        class="footer__delete"
        @click="onDelete"
      >
        <i class="fa fa-trash"></i>
      </button>
      <button
        type="submit"
        :disabled="!isSubmitButtonEnabled"
        class="footer__submit"
      >
        保存
      </button>
    </footer>
  </form>
</template>

<script>
import deepEqual from 'deep-equal';
import {
  getAncestorTagList,
  getAncestorTagsLength,
  getLongestDescendantsNum,
} from '../../../libs/tags';
import { restClient } from '../../../libs/restClient';

const TAG_MAX_DEPTH = 4;

const filterEditableTagField = (tag) => {
  if (tag === null) {
    return {
      name: '',
      description: '',
      parent_id: null,
    };
  }
  return {
    name: tag.name,
    description: tag.description,
    parent_id: tag.parent_id,
  };
};

const getParentCategoryName = (allCategories, tag) => {
  if (tag === null) {
    return '';
  }
  const parentCategory = allCategories.find(
    (category) => category.id === tag.parent_id
  );
  return parentCategory ? parentCategory.name : '';
};

export default {
  name: 'TagEditor',
  props: {
    tag: {
      type: Object,
      default: null,
    },
    allCategories: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      editingTag: filterEditableTagField(this.tag),
      editingParentCategoryName: getParentCategoryName(
        this.allCategories,
        this.tag
      ),
    };
  },
  watch: {
    editingParentCategoryName: {
      handler(newName) {
        if (newName === '') {
          this.editingTag.parent_id = null;
          return;
        }
        const category = this.allCategories.find(
          (category) => category.name === newName
        );
        if (category) {
          this.editingTag.parent_id = category.id;
        }
      },
      immediate: true,
    },
  },
  computed: {
    otherCategories() {
      if (this.tag === null) {
        return this.allCategories;
      }
      return this.allCategories.filter(
        (category) => category.id !== this.tag.id
      );
    },
    isCategory() {
      return this.tag && this.tag.tag_type !== 'tag';
    },
    tagPageLink() {
      if (!this.tag) {
        return '';
      }
      return this.isCategory
        ? `/category/${this.tag.name}`
        : `/tag/${this.tag.name}`;
    },
    isDeleteButtonEnabled() {
      return this.tag !== null;
    },
    isParentTagValid() {
      return (
        this.editingParentCategoryName === '' ||
        this.allCategories.some(
          (category) => category.name === this.editingParentCategoryName
        )
      );
    },
    hasMovedTagCircularReference() {
      if (!this.tag || !this.editingTag.parent_id) {
        return false;
      }

      const ancestorsOfMovedTag = getAncestorTagList(
        this.allCategories,
        this.editingTag.parent_id
      );
      return ancestorsOfMovedTag.some(
        (ancestor) => ancestor.id === this.tag.id
      );
    },
    isCategoryDepthUnderMaxSizeAfterMove() {
      if (
        this.tag === null ||
        !this.isCategory ||
        this.editingTag.parent_id === null
      ) {
        return true;
      }
      const fullDepthSize =
        getAncestorTagsLength(this.allCategories, this.editingTag.parent_id) +
        getLongestDescendantsNum(this.allCategories, this.tag.id) +
        1; // 自分の分
      return fullDepthSize <= TAG_MAX_DEPTH;
    },
    isSubmitButtonEnabled() {
      return (
        this.editingTag.name !== '' &&
        this.isParentTagValid &&
        this.isCategoryDepthUnderMaxSizeAfterMove &&
        !this.hasMovedTagCircularReference &&
        !deepEqual(this.editingTag, filterEditableTagField(this.tag))
      );
    },
  },
  methods: {
    async onDelete() {
      if (!confirm('本当に削除しますか？')) {
        return;
      }
      try {
        this.$root.$refs.loadModal.loading = false;
        await restClient.delete(`/api/tags/${this.tag.id}`);

        this.$emit('deleteTag', this.tag);
        this.$root.$refs.loadModal.success = true;
      } catch (error) {
        console.error(error);
        this.$root.$refs.loadModal.error = true;
      } finally {
        setTimeout(() => {
          this.$root.$refs.loadModal.loading = false;
          this.$root.$refs.loadModal.success = false;
          this.$root.$refs.loadModal.error = false;
        }, 1000);
      }
    },
    async onSubmit() {
      try {
        this.$root.$refs.loadModal.loading = true;
        const tag = {
          ...this.tag,
          ...this.editingTag,
        };
        const { data } = await restClient.put(`/api/tags/${tag.id}`, {
          tag,
        });

        this.$emit('updateTag', data);
        this.$root.$refs.loadModal.success = true;
      } catch (error) {
        console.error(error);
        this.$root.$refs.loadModal.error = true;
      } finally {
        setTimeout(() => {
          this.$root.$refs.loadModal.loading = false;
          this.$root.$refs.loadModal.success = false;
          this.$root.$refs.loadModal.error = false;
        }, 1000);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.form {
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 12px 14px 14px;

  &__title {
    font-size: 16px;
    font-weight: bold;
  }

  &__item {
    &:nth-child(n + 2) {
      margin-top: 12px;
    }
  }

  &__footer {
    &:nth-child(n + 2) {
      margin-top: 16px;
    }
  }
}

.form-item {
  display: block;

  &__title {
    font-size: 12px;
    color: #555;
  }

  &__input {
    font-size: 18px;
    width: 100%;

    &:nth-child(n + 2) {
      margin-top: 8px;
    }
  }

  &__alert {
    font-size: 12px;
    color: #f00;

    &:nth-child(n + 2) {
      margin-top: 8px;
    }
  }

  &__post-preview {
    &:nth-child(n + 2) {
      margin-top: 8px;
    }
  }
}

.footer {
  display: flex;
  justify-content: space-between;

  @mixin action {
    display: flex;
    align-items: center;
    border: 0;
    border-radius: 4px;
    padding: 0.4em 0.6em 0.3em;
    color: #fff;
    font-size: 18px;
    line-height: 1;

    &:hover {
      cursor: pointer;
    }

    &[disabled] {
      background-color: #eee;
      cursor: not-allowed;
    }

    span {
      font-size: 12px;

      &:nth-child(n + 2) {
        margin-left: 8px;
      }
    }
  }

  &__delete {
    @include action;

    flex: 0 0 auto;
    background-color: #f4695e;
  }

  &__submit {
    @include action;

    flex: 0 0 auto;
    background-color: #48c78e;
  }
}
</style>
